import React from 'react';
import { Link } from 'react-router-dom';
import { Award, Ship, Headphones, Truck, HeartHandshake, BadgeCheck, ArrowRight } from 'lucide-react';
import AnimatedSection from '../components/common/AnimatedSection';
import SEOHead from '../components/common/SEOHead';
import { companyInfo, differentials } from '../data/mock';

const iconMap = {
  Award,
  Ship,
  HeadphonesIcon: Headphones,
  Truck,
  HeartHandshake,
  BadgeCheck
};

const DifferentialsPage = () => {
  return (
    <>
      <SEOHead 
        title="Diferenciais"
        description={`Conheça os diferenciais da ${companyInfo.name}. ${companyInfo.experience}, importação direta, atendimento personalizado e muito mais.`}
        keywords="diferenciais Paulare, por que escolher Paulare, vantagens Paulare"
      />

      {/* Hero */}
      <section className="bg-gradient-to-br from-gray-900 via-gray-800 to-teal-900/30 py-20">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp">
            <span className="inline-flex items-center gap-2 bg-teal-500/20 text-teal-400 px-4 py-2 rounded-full text-sm font-medium mb-6">
              Por Que Nos Escolher
            </span>
            <h1 className="text-4xl md:text-5xl font-bold text-white mb-4">Nossos Diferenciais</h1>
            <p className="text-xl text-gray-400 max-w-2xl">
              {companyInfo.experience} e compromisso com a excelência fazem da Paulare a escolha certa.
            </p>
          </AnimatedSection>
        </div>
      </section>

      {/* Main Differentials */}
      <section className="py-24 bg-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {differentials.map((diff, index) => {
              const IconComponent = iconMap[diff.icon] || Award;
              return (
                <AnimatedSection key={diff.id} animation="fadeUp" delay={index * 100}>
                  <div className="group h-full p-8 bg-white rounded-2xl border border-gray-100 shadow-sm hover:shadow-xl hover:border-teal-200 transition-all duration-500">
                    <div className="w-16 h-16 bg-gradient-to-br from-teal-100 to-teal-50 rounded-2xl flex items-center justify-center mb-6 group-hover:scale-110 group-hover:bg-gradient-to-br group-hover:from-teal-500 group-hover:to-teal-600 transition-all duration-500">
                      <IconComponent size={32} className="text-teal-600 group-hover:text-white transition-colors duration-500" />
                    </div>
                    <h3 className="text-xl font-bold text-gray-900 mb-4">{diff.title}</h3>
                    <p className="text-gray-600 leading-relaxed">{diff.description}</p>
                  </div>
                </AnimatedSection>
              );
            })}
          </div>
        </div>
      </section>

      {/* Detailed Benefits */}
      <section className="py-24 bg-gray-50">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center mb-16">
            <h2 className="text-4xl md:text-5xl font-bold text-gray-900">O Que Nos Torna Únicos</h2>
          </AnimatedSection>

          <div className="space-y-12">
            {[
              {
                title: 'Experiência Comprovada',
                description: `Com ${companyInfo.experienceYears} anos no mercado, acumulamos conhecimento e expertise que nos permitem oferecer as melhores soluções para cada cliente. Nossa trajetória é marcada por parcerias duradouras e resultados consistentes.`,
                image: 'https://customer-assets.emergentagent.com/job_d9364d8b-3c3d-467f-aed0-dabbca109e0d/artifacts/s56y6wxl_AdobeStock_397707156%20%281%29.jpeg',
                reverse: false
              },
              {
                title: 'Importação Direta',
                description: 'Desde 2007, importamos diretamente da fábrica, eliminando intermediários e garantindo os melhores preços do mercado. Trabalhamos com as melhores marcas internacionais: MAXIMAL, HANGCHA e PALETRANS.',
                image: 'https://customer-assets.emergentagent.com/job_d9364d8b-3c3d-467f-aed0-dabbca109e0d/artifacts/dv7pd5ik_AdobeStock_753115090.jpeg',
                reverse: true
              },
              {
                title: 'Atendimento Personalizado',
                description: 'Cada cliente é único, e por isso oferecemos soluções individualizadas. Nossa equipe analisa suas necessidades específicas para recomendar os equipamentos mais adequados à sua operação.',
                image: 'https://customer-assets.emergentagent.com/job_d9364d8b-3c3d-467f-aed0-dabbca109e0d/artifacts/kb26dqfg_ChatGPT%20Image%2018%20de%20mar.%20de%202026%2C%2019_13_41.png',
                reverse: false
              }
            ].map((item, index) => (
              <AnimatedSection key={index} animation="fadeUp">
                <div className={`grid lg:grid-cols-2 gap-12 items-center ${item.reverse ? 'lg:flex-row-reverse' : ''}`}>
                  <div className={item.reverse ? 'lg:order-2' : ''}>
                    <img 
                      src={item.image} 
                      alt={item.title}
                      className="w-full rounded-2xl shadow-xl"
                    />
                  </div>
                  <div className={item.reverse ? 'lg:order-1' : ''}>
                    <h3 className="text-3xl font-bold text-gray-900 mb-4">{item.title}</h3>
                    <p className="text-lg text-gray-600 leading-relaxed">{item.description}</p>
                  </div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* Stats */}
      <section className="py-24 bg-gray-900 text-white">
        <div className="container mx-auto px-4">
          <div className="grid md:grid-cols-4 gap-8">
            {[
              { number: `${companyInfo.experienceYears}+`, label: 'Anos de Experiência' },
              { number: '1000+', label: 'Clientes Satisfeitos' },
              { number: '25t', label: 'Capacidade Máxima' },
              { number: '48h', label: 'Tempo de Resposta' }
            ].map((stat, index) => (
              <AnimatedSection key={index} animation="scaleUp" delay={index * 100}>
                <div className="text-center p-8 bg-gray-800/50 rounded-2xl">
                  <div className="text-5xl font-bold text-teal-400 mb-2">{stat.number}</div>
                  <div className="text-gray-400">{stat.label}</div>
                </div>
              </AnimatedSection>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-gradient-to-r from-teal-500 to-teal-600">
        <div className="container mx-auto px-4">
          <AnimatedSection animation="fadeUp" className="text-center">
            <h2 className="text-3xl md:text-4xl font-bold text-white mb-6">
              Pronto para Conhecer Nossa Qualidade?
            </h2>
            <Link
              to="/contato"
              className="inline-flex items-center gap-2 bg-white text-teal-600 px-8 py-4 rounded-xl font-bold hover:bg-gray-100 hover:scale-105 transition-all duration-300 shadow-lg"
            >
              Entre em Contato
              <ArrowRight size={20} />
            </Link>
          </AnimatedSection>
        </div>
      </section>
    </>
  );
};

export default DifferentialsPage;
